// ============================================================================
// OrgMobileList — vue focus incrementale mobile (NAV v3)
// ============================================================================
// Manager cliquable + carte focus + reports avec bouton + (apercu sans changer le focus).

import { ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GS_COMPONENTS, StatusBadge } from '@gs/ui';
import {
  collectPathEmails,
  directOrgReports,
  findOrgNode,
  type OrgNode,
} from '../../lib/org/hierarchy';
import { optionalJobTitle } from '../../lib/org/derivePersonRole';
import { orgLevelI18nKey } from '../../lib/org/deriveOrgLevel';
import { shouldShowOrgTeamName } from '../../lib/org/normalizeOrgLabel';
import { getInitials } from '../../lib/profile/initials';
import { useTranslation } from '../../i18n';

/** Badge texte explicite pour l'echelon d'entreprise (deriveOrgLevel). */
function PersonOrgLevelBadge({ node }: { node: OrgNode }) {
  const { t } = useTranslation();
  return (
    <StatusBadge variant="neutral">{t(orgLevelI18nKey(node.role))}</StatusBadge>
  );
}

function PersonInitials({
  node,
  size = 'md',
}: {
  node: OrgNode;
  size?: 'md' | 'sm' | 'lg';
}) {
  const cls =
    size === 'lg'
      ? 'h-12 w-12 text-field'
      : size === 'sm'
        ? 'h-8 w-8 text-chip'
        : 'h-10 w-10 text-chip';
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 ${cls} ${
        node.is_team_director ? 'ring-2 ring-brand-500/40' : ''
      }`}
    >
      {getInitials(node.full_name, node.email)}
    </span>
  );
}

function getManagerNode(roots: OrgNode[], focusedEmail: string): OrgNode | null {
  const path = collectPathEmails(roots, focusedEmail);
  if (path.length < 2) return null;
  return findOrgNode(roots, path[path.length - 2]);
}

export function OrgMobileList({
  roots,
  focusedEmail,
  highlightedEmail,
  previewEmail,
  onFocus,
  onPreviewToggle,
}: {
  roots: OrgNode[];
  focusedEmail: string;
  highlightedEmail?: string | null;
  previewEmail?: string | null;
  onFocus: (email: string) => void;
  onPreviewToggle: (email: string | null) => void;
}) {
  const { t } = useTranslation();
  const focusedNode = findOrgNode(roots, focusedEmail);
  if (!focusedNode) return null;

  const manager = getManagerNode(roots, focusedEmail);
  const reports = directOrgReports(focusedNode);

  return (
    <div className="space-y-3 md:hidden">
      <div className={`${GS_COMPONENTS.card} p-3 text-center`}>
        {shouldShowOrgTeamName(
          focusedNode.team_name,
          t(orgLevelI18nKey(focusedNode.role)),
        ) ? (
          <p className="text-field font-semibold text-slate-800 dark:text-slate-100">
            {focusedNode.team_name}
          </p>
        ) : null}
        <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">
          {t('gest:organigramme.focus.branchCount', { count: focusedNode.descendant_count })}
        </p>
      </div>

      {manager ? (
        <>
          <p className="text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            {t('gest:organigramme.focus.managerLabel')}
          </p>
          <button
            type="button"
            className="flex w-full touch-manipulation items-center gap-2.5 rounded-xl border border-slate-200 bg-white p-3 text-left shadow-xs dark:border-slate-700 dark:bg-slate-800"
            onClick={() => onFocus(manager.email)}
          >
            <PersonInitials node={manager} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-field font-semibold text-slate-900 dark:text-slate-100">
                {manager.full_name ?? manager.email}
              </span>
              <PersonOrgLevelBadge node={manager} />
              {optionalJobTitle(manager.job_title) ? (
                <span className="block truncate text-meta text-slate-500 dark:text-slate-400">
                  {optionalJobTitle(manager.job_title)}
                </span>
              ) : null}
            </span>
            <ChevronUp size={18} aria-hidden="true" className="shrink-0 text-slate-400" />
          </button>
        </>
      ) : null}

      <div
        className="flex items-center gap-3 rounded-xl border border-brand-500 bg-white p-3.5 shadow-xs dark:bg-slate-800"
        role="status"
        aria-live="polite"
      >
        <PersonInitials node={focusedNode} size="lg" />
        <div className="min-w-0 flex-1">
          {focusedNode.href ? (
            <Link
              to={focusedNode.href}
              className="block truncate text-body font-semibold text-slate-900 underline-offset-2 hover:underline dark:text-slate-100"
            >
              {focusedNode.full_name ?? focusedNode.email}
            </Link>
          ) : (
            <p className="truncate text-body font-semibold text-slate-900 dark:text-slate-100">
              {focusedNode.full_name ?? focusedNode.email}
            </p>
          )}
          <PersonOrgLevelBadge node={focusedNode} />
          {optionalJobTitle(focusedNode.job_title) ? (
            <span className="block truncate text-meta text-slate-500 dark:text-slate-400">
              {optionalJobTitle(focusedNode.job_title)}
            </span>
          ) : null}
        </div>
      </div>

      <p className="text-meta font-semibold text-slate-500 dark:text-slate-400">
        {t('gest:organigramme.focus.reportsLabel', { count: reports.length })}
      </p>

      {reports.length === 0 ? (
        <p className={`${GS_COMPONENTS.card} p-4 text-field text-slate-500 dark:text-slate-400`}>
          {t('gest:organigramme.focus.noReports')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {reports.map((node) => {
            const subReports = directOrgReports(node);
            const previewOpen = previewEmail === node.email;
            const isHighlighted = highlightedEmail === node.email;

            return (
              <li
                key={node.email}
                className={`overflow-hidden rounded-xl border bg-white shadow-xs dark:bg-slate-800 ${
                  isHighlighted
                    ? 'border-brand-500 ring-1 ring-brand-500'
                    : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                <div className="flex items-center gap-2 px-3.5 py-3">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 touch-manipulation items-center gap-2.5 text-left"
                    onClick={() => onFocus(node.email)}
                  >
                    <PersonInitials node={node} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-field font-semibold text-slate-900 dark:text-slate-100">
                        {node.full_name ?? node.email}
                      </span>
                      <PersonOrgLevelBadge node={node} />
                      {optionalJobTitle(node.job_title) ? (
                        <span className="block truncate text-meta text-slate-500 dark:text-slate-400">
                          {optionalJobTitle(node.job_title)}
                        </span>
                      ) : null}
                      {shouldShowOrgTeamName(
                        node.team_name,
                        t(orgLevelI18nKey(node.role)),
                      ) ? (
                        <span className="block truncate text-meta text-slate-500 dark:text-slate-400">
                          {node.team_name}
                        </span>
                      ) : null}
                    </span>
                  </button>
                  {subReports.length > 0 ? (
                    <span className="flex shrink-0 items-center gap-1.5">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-chip tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                        {subReports.length}
                      </span>
                      <button
                        type="button"
                        aria-expanded={previewOpen}
                        aria-label={
                          previewOpen
                            ? t('gest:organigramme.focus.deepenClose', {
                                name: node.full_name ?? node.email,
                              })
                            : t('gest:organigramme.focus.deepenPreview', {
                                name: node.full_name ?? node.email,
                              })
                        }
                        className={`inline-flex h-7 min-w-7 touch-manipulation items-center justify-center rounded-full border text-field font-bold ${
                          previewOpen
                            ? 'border-brand-500 bg-brand-500 text-white'
                            : 'border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}
                        onClick={() => onPreviewToggle(previewOpen ? null : node.email)}
                      >
                        +
                      </button>
                    </span>
                  ) : null}
                </div>
                {previewOpen && subReports.length > 0 ? (
                  <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/40">
                    <p className="px-2 pb-2 text-chip text-slate-400 dark:text-slate-500">
                      {t('gest:organigramme.focus.previewLabel', {
                        name: node.full_name ?? node.email,
                      })}
                    </p>
                    <ul className="flex flex-col gap-1">
                      {subReports.map((child) => (
                        <li key={child.email}>
                          <button
                            type="button"
                            onClick={() => onFocus(child.email)}
                            className="flex w-full touch-manipulation items-center gap-2.5 rounded-lg border border-dashed border-slate-300 bg-white px-2 py-2 text-left dark:border-slate-600 dark:bg-slate-800"
                          >
                            <PersonInitials node={child} size="sm" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-field font-medium text-slate-800 dark:text-slate-100">
                                {child.full_name ?? child.email}
                              </span>
                              <span className="block truncate text-chip text-slate-400">
                                {t(orgLevelI18nKey(child.role))}
                                {optionalJobTitle(child.job_title)
                                  ? ` · ${optionalJobTitle(child.job_title)}`
                                  : ''}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
